import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ImageUp, Trash2, Loader2 } from "lucide-react";
import { uploadArtFile } from "@/lib/uploadArt";

// Reads an image file, returns its natural pixel ratio + a small downscaled thumbnail (data URL).
const readImage = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const nw = img.naturalWidth || 1;
      const nh = img.naturalHeight || 1;
      const max = 240;
      const scale = Math.min(1, max / Math.max(nw, nh));
      const cw = Math.max(1, Math.round(nw * scale));
      const ch = Math.max(1, Math.round(nh * scale));
      const cv = document.createElement("canvas");
      cv.width = cw;
      cv.height = ch;
      let thumb = url;
      try {
        const ctx = cv.getContext("2d");
        ctx.drawImage(img, 0, 0, cw, ch);
        thumb = cv.toDataURL("image/jpeg", 0.72);
      } catch (e) { /* tainted canvas — fall back to object URL */ }
      URL.revokeObjectURL(url);
      resolve({ name: file.name.replace(/\.[^.]+$/, ""), rw: nw, rh: nh, thumb });
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

// Bulk-import artwork images. Each image becomes a size row (in file order) with a locked
// aspect ratio read from the image pixels — the user then types one dimension and the other fills in.
export default function LFImageImport({ onApply }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [zoom, setZoom] = useState(null);

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setLoading(true);
    try {
      const out = (await Promise.all(files.map(async (file) => {
        const meta = await readImage(file);
        if (!meta) return null;
        let art_id = null;
        try { const up = await uploadArtFile(file); art_id = up.file_id; } catch (e) { /* storage optional — keep working with the local thumbnail */ }
        return { ...meta, art_id };
      }))).filter(Boolean);
      if (!out.length) { toast.error("Could not read those images"); setLoading(false); return; }
      setRows((prev) => [...(prev || []), ...out].slice(0, 25));
    } catch (err) { toast.error("Could not read those images"); }
    setLoading(false);
  };
  const rm = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const apply = (replace) => {
    if (!rows || !rows.length) { toast.error("Add at least one image"); return; }
    onApply(rows, replace);
    toast.success(`${replace ? "Replaced with" : "Added"} ${rows.length} artwork${rows.length > 1 ? "s" : ""}`);
    setOpen(false);
    setRows(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setRows(null); }}>
      <DialogTrigger asChild>
        <Button data-testid="lf-import-images-btn" size="sm" variant="outline" className="rounded-lg h-9 text-xs">
          <ImageUp size={14} className="mr-1" />Import Images
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="lf-image-dialog">
        <DialogHeader><DialogTitle>Import artwork images</DialogTitle>
          <DialogDescription>Upload logo/artwork files to create size rows with their proportion locked.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[12px] text-slate-500">
            Select the logo/artwork files (in order). Each becomes a size row with its proportion locked —
            type one dimension and the other fills automatically.
          </p>
          <label className="flex items-center justify-center gap-2 h-24 rounded-lg border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#2495D3] text-sm text-slate-500" data-testid="lf-image-drop">
            {loading ? <><Loader2 size={16} className="animate-spin" />Reading images…</> : <><ImageUp size={18} />Upload PNG / JPG / WEBP (multiple)</>}
            <input data-testid="lf-image-file" type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={pick} className="hidden" />
          </label>
          {rows && rows.length > 0 && (
            <div className="border border-slate-200 rounded-lg p-3 max-h-72 overflow-auto" data-testid="lf-image-review">
              <div className="grid grid-cols-12 gap-2 text-[10px] font-mono uppercase text-slate-400 mb-1">
                <span className="col-span-1">#</span><span className="col-span-2">Art</span><span className="col-span-6">Name</span><span className="col-span-2">Ratio</span><span />
              </div>
              {rows.map((r, i) => (
                <div key={i} data-testid="lf-image-row" className="grid grid-cols-12 gap-2 items-center mb-1">
                  <span className="col-span-1 text-xs font-mono text-slate-400">{i + 1}</span>
                  <span className="col-span-2">
                    <img src={r.thumb} alt="" onClick={() => setZoom(r.thumb)} className="h-9 w-9 object-contain rounded border border-slate-200 bg-white cursor-zoom-in hover:ring-2 hover:ring-[#2495D3]" data-testid={`lf-image-thumb-${i}`} />
                  </span>
                  <span className="col-span-6 truncate text-xs text-slate-600" title={r.name}>{r.name}</span>
                  <span className="col-span-2 text-[11px] font-mono text-slate-500">{(r.rw / r.rh).toFixed(2)}:1</span>
                  <button className="col-span-1 text-slate-400 hover:text-red-500" onClick={() => rm(i)} data-testid={`lf-image-remove-${i}`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        {rows && rows.length > 0 && (
          <DialogFooter className="gap-2">
            <Button data-testid="lf-image-replace" variant="outline" className="rounded-lg" onClick={() => apply(true)}>Replace sizes</Button>
            <Button data-testid="lf-image-add" className="rounded-lg" onClick={() => apply(false)}>Add to sizes</Button>
          </DialogFooter>
        )}
        {zoom && (
          <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-6 cursor-zoom-out" onClick={() => setZoom(null)} data-testid="lf-image-zoom">
            <img src={zoom} alt="artwork preview" className="max-h-[88vh] max-w-[88vw] rounded-lg shadow-2xl bg-white" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
